import { useCallback, useState } from "react";
import { Image, ImageSourcePropType, Text, View } from "react-native";
import { useSlot, useStyled } from "use-styled";

const AvatarRoot = useStyled(View, {
  base: {
    className:
      "rounded-full flex-row justify-center items-center bg-teachLoop-accentPrimaryDisabled",
    style: {},
  },
  variants: {
    size: {
      sm: { className: "h-6 w-6" },
      md: { className: "h-16 w-16" },
      lg: { className: "h-32 w-32" },
      base: { className: "h-[48] w-[48]" },
    },
  },
  defaultVariants: {},
  compoundVariants: [],
});

const AvatarLabel = useStyled(Text, {
  base: {
    className: "font-geologica-medium font-bold",
  },
  variants: {
    size: {
      sm: { className: "text-[9px]" },
      md: { className: "text-[18px]" },
      lg: { className: "text-[36px]" },
      base: { className: "text-[24px]" },
    },
  },
  defaultVariants: {
    size: "md",
  },
});

const AvatarImage = useStyled(Image, {
  base: {
    className: "h-full w-full rounded-full",
    style: { resizeMode: "cover" },
  },
  props: true,
  variants: {
    size: {
      sm: { style: { height: 24, width: 24 } },
      base: { style: { height: 48, width: 48 } },
      md: { style: { height: 64, width: 64 } },
      lg: { style: { height: 128, width: 128 } },
    },
  },
});

type AvatarContentProps = {
  source?: ImageSourcePropType;
  label?: string;
  labelSize: "sm" | "md" | "lg" | "base";
  size: "sm" | "md" | "lg" | "base";
};

function AvatarContent({ source, label, labelSize, size }: AvatarContentProps) {
  const [error, setError] = useState(false);

  const handleError = useCallback(() => {
    setError(true);
  }, []);

  const handleLoad = useCallback(() => {
    setError(false);
  }, []);

  const getInitialsFromLabel = useCallback((label?: string) => {
    if (!label) return "";
    return label
      .split(" ")
      .map((word) => word[0])
      .slice(0, 2)
      .join("");
  }, []);

  return !!source && !error ? (
    <AvatarImage
      source={source}
      onLoad={handleLoad}
      onError={handleError}
      size={size}
    />
  ) : (
    <AvatarLabel size={labelSize}>{getInitialsFromLabel(label)}</AvatarLabel>
  );
}

export const Avatar = useSlot(AvatarRoot, {
  Content: AvatarContent,
});
